/**
 * Cover de repli d'un exercice relié à une SKILLCARD, quand il n'a pas
 * d'illustration : fond et créature origami dans la teinte de la carte, titre
 * de la carte, niveau(x). Remplit son conteneur (même format que la cover
 * illustrée : `aspect-video` dans l'en-tête du player). Partagée par le
 * player V2 de l'app et le showcase du site.
 */

import type { CSSProperties } from 'react';
import { cardPalette, stripMath, type CardColorTheme } from '@jeraisonne/db/cards';
import { MathText } from '../content/math-text';
import { OrigamiCreature } from './origami-creature';
import { cardLevelRange } from './verso-stats';

/** Ce que la cover lit d'une SKILLCARD (lien d'exercice côté app, showcase côté site). */
export type SkillcardCoverCard = {
  title: string;
  levels: readonly string[];
  color_theme: CardColorTheme | null;
  /** Spec brute de la créature (`cards.creature`), validée au rendu. */
  creature: unknown;
};

export function SkillcardExerciseCover({
  card,
  exerciseTitle,
}: {
  card: SkillcardCoverCard;
  /** Titre de l'exercice, rappelé sous celui de la carte s'il est différent. */
  exerciseTitle?: string;
}) {
  const palette = cardPalette(card.levels, card.color_theme);
  const levels = cardLevelRange(card.levels);
  // Teinte de la carte en variables CSS (seules valeurs non statiques).
  const vars = { '--jr-c-accent': palette.accent, '--jr-c-bg': palette.bg } as CSSProperties;
  const subtitle =
    exerciseTitle && stripMath(exerciseTitle) !== stripMath(card.title) ? exerciseTitle : null;

  return (
    <div
      data-testid="skillcard-cover"
      style={vars}
      className="absolute inset-0 flex items-center gap-4 overflow-hidden bg-[radial-gradient(circle_at_25%_50%,color-mix(in_srgb,var(--jr-c-accent)_45%,var(--jr-c-bg))_0%,var(--jr-c-bg)_70%)] px-6 pb-8 pt-16 sm:gap-8 sm:px-10"
    >
      <OrigamiCreature
        creature={card.creature}
        accent={palette.accent}
        className="aspect-square h-[70%] max-h-56 shrink-0 drop-shadow-[0_8px_18px_rgba(0,0,0,0.45)]"
      />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-display text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--jr-c-accent)]">
          SKILLCARD
        </p>
        <p className="font-display text-xl font-extrabold leading-tight text-white drop-shadow sm:text-3xl">
          <MathText>{card.title}</MathText>
        </p>
        {subtitle && (
          <p className="line-clamp-2 text-sm font-semibold text-white/75">
            <MathText>{subtitle}</MathText>
          </p>
        )}
        {levels && (
          <span className="inline-flex rounded-full border border-[var(--jr-c-accent)] bg-[color-mix(in_srgb,var(--jr-c-accent)_22%,transparent)] px-3 py-1 text-xs font-bold text-white">
            {levels}
          </span>
        )}
      </div>
    </div>
  );
}
